import React from 'react';

import { Link } from 'react-router-dom';

import { css } from '@emotion/css';
import { ModifiedUpdatedMinMaxDTO } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTaskSummary';
import { Typography } from 'antd';

import { ReplenishmentMethod } from 'pages/purchasing/utils';

import { AsyncTable } from 'components/AsyncTable';
import { FlexSpace } from 'components/FlexSpace';
import { ChangeDeltaWithArrow } from 'components/recipes/ChangeDeltaWithArrow';

import { formatNumber, formatPercent } from 'utils/formatting';
import { makePath, routePaths } from 'utils/routes';
import { asKeyOf, sortableChangePercentColumn } from 'utils/tables';

type MultiUpdateReplenishmentSettingsAsyncTableProps = {
  currentReplenishmentMethod: ReplenishmentMethod | null;
  tableData: ModifiedUpdatedMinMaxDTO[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
};

export function MultiUpdateReplenishmentSettingsAsyncTable({
  currentReplenishmentMethod,
  tableData,
  isLoading,
  currentPage,
  setCurrentPage,
}: MultiUpdateReplenishmentSettingsAsyncTableProps) {
  type TableData = ModifiedUpdatedMinMaxDTO & {
    automated: boolean;
    locationDisplayName: string;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'itemId',
      key: 'itemId',
      width: 200,
      fixed: 'left' as const,
      sorter: true,
      render: (itemId: string, item: TableData) => (
        <div>
          <Link
            target="_blank"
            to={makePath(routePaths.demandPlanning.planning, null, {
              query: item.itemCode,
              where: {
                location: [item.locationDisplayName],
              },
            })}
          >
            {item.itemCode}
          </Link>
          <div>{item.itemName}</div>
        </div>
      ),
    },
    {
      title: 'Location',
      dataIndex: asKeyOf<TableData>('locationId'),
      key: 'locationId',
      width: 200,
      sorter: true,
      render: (locationId: string, record: TableData) => (
        <Typography.Text>{record?.locationDisplayName}</Typography.Text>
      ),
    },

    sortableChangePercentColumn({
      title: currentReplenishmentMethod === ReplenishmentMethod.MinMax ? 'Minimum' : 'Order Point',
      dataIndex: asKeyOf<TableData>('newMin'),
      align: 'left' as const,
      width: 200,
      render: (newMin, record) => {
        const delta =
          record.currentMin === 0 ? undefined : newMin === 0 ? -1 : (newMin - record.currentMin) / record.currentMin;
        return (
          <FlexSpace direction="row" gap={0} justify="space-between">
            <div>
              <Typography.Text style={{ fontWeight: 400 }}>{formatNumber(record.currentMin)}</Typography.Text>
              <Typography.Text style={{ fontWeight: 700 }}>
                {' -> '}
                {formatNumber(newMin)}
              </Typography.Text>
            </div>
            <FlexSpace gap={0}>
              <div>[</div>
              <ChangeDeltaWithArrow
                value={delta}
                valueFormatter={(value) => formatPercent((value || 0).toPrecision(2))}
                customArrowCss={css`
                  color: inherit;
                `}
              />
              <div>]</div>
            </FlexSpace>
          </FlexSpace>
        );
      },
    }),
    sortableChangePercentColumn({
      title: currentReplenishmentMethod === ReplenishmentMethod.MinMax ? 'Maximum' : 'Order Quantity',
      dataIndex: asKeyOf<TableData>('newMax'),
      align: 'left' as const,
      width: 200,
      render: (newMax, record) => {
        const delta =
          record.currentMax === 0 ? undefined : newMax === 0 ? -1 : (newMax - record.currentMax) / record.currentMax;
        return (
          <FlexSpace direction="row" gap={0} justify="space-between">
            <div>
              <Typography.Text style={{ fontWeight: 400 }}>{formatNumber(record.currentMax)}</Typography.Text>
              <Typography.Text style={{ fontWeight: 700 }}>
                {' -> '}
                {formatNumber(newMax)}
              </Typography.Text>
            </div>
            <FlexSpace gap={0}>
              <div>[</div>
              <ChangeDeltaWithArrow
                value={delta}
                valueFormatter={(value) => formatPercent((value || 0).toPrecision(2))}
                customArrowCss={css`
                  color: inherit;
                `}
              />
              <div>]</div>
            </FlexSpace>
          </FlexSpace>
        );
      },
    }),
  ];

  return (
    <AsyncTable
      tableProps={{
        isLoading,
        items: tableData,
        totalCount: tableData.length,
        pageSize: 10,
        page: currentPage,
        error: undefined,
        setPage: setCurrentPage,
        reload: () => {},
        isReloading: false,
      }}
      columns={columns}
    />
  );
}
